package com.havn.app.features.followups.presentation

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.presentation.havnFriendlyMessage
import com.havn.app.core.localization.HavnLanguage
import com.havn.app.core.localization.HavnLanguageRepository
import com.havn.app.core.media.decodeHavnDataUrlBytes
import com.havn.app.core.media.havnDataUrlContentType
import com.havn.app.core.platform.HavnExternalActions
import com.havn.app.core.platform.HavnEmailAttachment
import com.havn.app.features.attachments.domain.AttachmentRepository
import com.havn.app.features.attachments.domain.HavnAttachment
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.features.followups.domain.AiFollowUpDraft
import com.havn.app.features.followups.domain.AiFollowUpInput
import com.havn.app.features.followups.domain.AiFollowUpService
import com.havn.app.features.followups.domain.FollowUp
import com.havn.app.features.followups.domain.FollowUpChannel
import com.havn.app.features.followups.domain.FollowUpDraft
import com.havn.app.features.followups.domain.FollowUpRepository
import com.havn.app.features.followups.domain.FollowUpStatus
import com.havn.app.features.leads.domain.Lead
import com.havn.app.features.leads.domain.LeadRepository
import io.ktor.client.HttpClient
import io.ktor.client.request.get
import io.ktor.client.statement.bodyAsBytes
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

class FollowUpsViewModel(
    private val followUpRepository: FollowUpRepository,
    private val leadRepository: LeadRepository,
    private val aiFollowUpService: AiFollowUpService,
    private val attachmentRepository: AttachmentRepository,
    private val authRepository: AuthRepository,
    private val languageRepository: HavnLanguageRepository,
    private val httpClient: HttpClient = HttpClient(),
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
) {
    private val _uiState = MutableStateFlow(FollowUpsUiState())
    val uiState: StateFlow<FollowUpsUiState> = _uiState.asStateFlow()

    private var agentId: String? = null
    private var agentName: String = "HAVN agent"
    private var language: HavnLanguage = HavnLanguage.English

    init {
        scope.launch {
            languageRepository.language.collect { selectedLanguage ->
                language = selectedLanguage
                _uiState.value = _uiState.value.copy(
                    form = _uiState.value.form.copy(
                        languageCode = selectedLanguage.code,
                        languageName = selectedLanguage.displayName,
                        languageNativeName = selectedLanguage.nativeName
                    )
                )
            }
        }
        scope.launch {
            authRepository.authState.collect { state ->
                val nextAgentId = (state as? AuthState.LoggedIn)?.user?.id
                agentId = nextAgentId
                if (nextAgentId == null) {
                    _uiState.value = FollowUpsUiState()
                } else {
                    agentName = state.user.email?.substringBefore("@")?.takeIf { it.isNotBlank() } ?: "HAVN agent"
                    scope.launch {
                        authRepository.agentProfile(nextAgentId).onSuccess { profile ->
                            agentName = profile.fullName.ifBlank { profile.email?.substringBefore("@").orEmpty() }.ifBlank { "HAVN agent" }
                            _uiState.value = _uiState.value.copy(form = _uiState.value.form.copy(agentName = agentName))
                        }
                    }
                    loadData(nextAgentId)
                }
            }
        }
    }

    fun openGenerator() {
        _uiState.value = _uiState.value.copy(mode = FollowUpsMode.Generate, error = null)
    }

    fun openSendFollowUpForLead(leadId: String) {
        _uiState.value = _uiState.value.copy(
            mode = FollowUpsMode.Generate,
            selectedLeadId = leadId,
            generatedDraft = null,
            subjectOverride = "",
            bodyOverride = "",
            availablePhotoDataUrls = emptyList(),
            selectedPhotoDataUrls = emptyList(),
            availablePropertyFiles = emptyList(),
            selectedPropertyFileIds = emptyList(),
            error = null
        )
        selectLead(leadId)
    }

    fun openSendFollowUpForLead(lead: Lead) {
        val currentState = _uiState.value
        val sameLeadAlreadyOpen = currentState.mode == FollowUpsMode.Generate && currentState.selectedLeadId == lead.id
        val existingLeads = _uiState.value.leads
        val updatedLeads = if (existingLeads.any { it.id == lead.id }) {
            existingLeads.map { existing ->
                if (existing.id == lead.id) {
                    lead.withPreservedPhotos(existing)
                } else {
                    existing
                }
            }
        } else {
            listOf(lead) + existingLeads
        }
        val availablePhotos = lead.sourceProperty.photoDataUrls.ifEmpty {
            currentState.availablePhotoDataUrls.takeIf { sameLeadAlreadyOpen }
                ?: existingLeads.firstOrNull { it.id == lead.id }?.sourceProperty?.photoDataUrls
                ?: emptyList()
        }
        val selectedPhotos = if (sameLeadAlreadyOpen) {
            currentState.selectedPhotoDataUrls.filter { it in availablePhotos }
                .ifEmpty { availablePhotos }
        } else {
            availablePhotos
        }
        _uiState.value = _uiState.value.copy(
            mode = FollowUpsMode.Generate,
            selectedLeadId = lead.id,
            leads = updatedLeads,
            generatedDraft = if (sameLeadAlreadyOpen) currentState.generatedDraft else null,
            subjectOverride = if (sameLeadAlreadyOpen) currentState.subjectOverride else "",
            bodyOverride = if (sameLeadAlreadyOpen) currentState.bodyOverride else "",
            availablePhotoDataUrls = availablePhotos,
            selectedPhotoDataUrls = selectedPhotos,
            selectedPropertyFileIds = if (sameLeadAlreadyOpen) currentState.selectedPropertyFileIds else emptyList(),
            error = null
        )
        applyLeadSelection(lead.withPhotoDataUrls(availablePhotos), preserveDraft = sameLeadAlreadyOpen)
    }

    fun openQueue() {
        _uiState.value = _uiState.value.copy(mode = FollowUpsMode.Queue, error = null)
        refresh()
    }

    fun refresh() {
        val currentAgentId = agentId ?: return
        scope.launch { loadData(currentAgentId) }
    }

    fun selectLead(leadId: String) {
        val lead = _uiState.value.leads.firstOrNull { it.id == leadId } ?: return
        applyLeadSelection(lead)
    }

    private fun applyLeadSelection(lead: Lead, preserveDraft: Boolean = false) {
        val state = _uiState.value
        val availablePhotos = lead.sourceProperty.photoDataUrls.ifEmpty {
            state.availablePhotoDataUrls.takeIf { state.selectedLeadId == lead.id } ?: emptyList()
        }
        val selectedPhotos = if (preserveDraft && state.selectedLeadId == lead.id) {
            state.selectedPhotoDataUrls.filter { it in availablePhotos }.ifEmpty { availablePhotos }
        } else {
            availablePhotos
        }
        _uiState.value = _uiState.value.copy(
            selectedLeadId = lead.id,
            form = _uiState.value.form.copy(
                visitorName = lead.fullName,
                propertyAddress = if (lead.isBuyerInquiry) lead.desiredLocation.orEmpty() else lead.sourceProperty.address,
                visitorInterest = if (lead.isBuyerInquiry) {
                    listOfNotNull(
                        "General inquiry for ${lead.desiredLocation ?: "a property"}",
                        lead.locationRadiusKm?.let { "$it km radius" },
                        lead.desiredPropertyType?.let { "property type: $it" },
                        listOfNotNull(lead.budgetMin, lead.budgetMax).takeIf { it.isNotEmpty() }?.joinToString("–")?.let { "budget: $it" },
                        lead.purchaseTimeline?.let { "timeline: $it" },
                        lead.source?.let { "lead source: $it" }
                    ).joinToString("; ")
                } else if (lead.isInquiry) {
                    "Property inquiry about ${lead.sourceProperty.title}; lead source: ${lead.source ?: "unspecified"}"
                } else {
                    "Interested in ${lead.sourceProperty.title}; lead source: ${lead.source ?: "unspecified"}"
                },
                notes = lead.notes.orEmpty(),
                leadStatus = if (lead.isInquiry) "Inquiry status: ${lead.temperature.label}" else lead.temperature.label,
                agentName = agentName,
                languageCode = language.code,
                languageName = language.displayName,
                languageNativeName = language.nativeName
            ),
            availablePhotoDataUrls = availablePhotos,
            selectedPhotoDataUrls = selectedPhotos,
            selectedPropertyFileIds = if (preserveDraft && state.selectedLeadId == lead.id) state.selectedPropertyFileIds else emptyList(),
            error = null
        )
        lead.propertyId?.takeIf { it.isNotBlank() }?.let(::loadPropertyFiles) ?: run {
            _uiState.value = _uiState.value.copy(
                availablePropertyFiles = emptyList(),
                selectedPropertyFileIds = emptyList()
            )
        }
    }

    private fun loadPropertyFiles(propertyId: String) {
        scope.launch {
            attachmentRepository.attachmentsForContext(propertyId = propertyId)
                .onSuccess { files ->
                    _uiState.value = _uiState.value.let { state ->
                        state.copy(
                            availablePropertyFiles = files,
                            selectedPropertyFileIds = state.selectedPropertyFileIds.filter { selectedId ->
                                files.any { it.id == selectedId }
                            }
                        )
                    }
                }
                .onFailure { HavnLogger.error("FollowUpsViewModel", "Failed to load property files for follow-up", it) }
        }
    }

    fun updateForm(transform: FollowUpGenerationForm.() -> FollowUpGenerationForm) {
        _uiState.value = _uiState.value.copy(form = _uiState.value.form.transform(), error = null)
    }

    fun generateDraft() {
        generateDraft(openPreview = true)
    }

    fun generateInlineDraft() {
        generateDraft(openPreview = false)
    }

    private fun generateDraft(openPreview: Boolean) {
        val validation = _uiState.value.form.validateForGeneration()
        if (validation != null) {
            _uiState.value = _uiState.value.copy(error = validation)
            return
        }

        val input = _uiState.value.form.toAiInput(_uiState.value.selectedMessageType)
        _uiState.value = _uiState.value.copy(isGenerating = true, error = null)
        scope.launch {
            aiFollowUpService.generateDraft(input)
                .onSuccess { draft ->
                    _uiState.value = _uiState.value.copy(
                        isGenerating = false,
                        generatedDraft = draft,
                        previewChannel = FollowUpChannel.Email,
                        subjectOverride = draft.emailSubject,
                        bodyOverride = draft.emailDraft,
                        mode = if (openPreview) FollowUpsMode.Preview else FollowUpsMode.Generate
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isGenerating = false)
                    setError(it)
                }
        }
    }

    fun selectPreviewChannel(channel: FollowUpChannel) {
        _uiState.value = _uiState.value.copy(previewChannel = channel, error = null)
    }

    fun selectMessageType(type: FollowUpMessageType) {
        _uiState.value = _uiState.value.copy(
            selectedMessageType = type,
            previewChannel = FollowUpChannel.Email,
            generatedDraft = null,
            subjectOverride = "",
            bodyOverride = "",
            error = null
        )
    }

    fun toggleSelectedPhoto(photoDataUrl: String) {
        val state = _uiState.value
        val selectedPhotos = state.selectedPhotoDataUrls
        _uiState.value = state.copy(
            selectedPhotoDataUrls = if (photoDataUrl in selectedPhotos) {
                selectedPhotos - photoDataUrl
            } else {
                selectedPhotos + photoDataUrl
            },
            error = null
        )
    }

    fun toggleSelectedPropertyFile(fileId: String) {
        val state = _uiState.value
        val selectedFiles = state.selectedPropertyFileIds
        _uiState.value = state.copy(
            selectedPropertyFileIds = if (fileId in selectedFiles) {
                selectedFiles - fileId
            } else {
                selectedFiles + fileId
            },
            error = null
        )
    }

    fun updatePreviewSubject(subject: String) {
        _uiState.value = _uiState.value.copy(subjectOverride = subject, error = null)
    }

    fun updatePreviewBody(body: String) {
        _uiState.value = _uiState.value.copy(
            bodyOverride = body,
            error = null
        )
    }

    fun saveSelectedDraft() {
        saveSelectedDraftForChannel(_uiState.value.previewChannel)
    }

    fun sendSelectedDraftEmail() {
        val state = _uiState.value
        val lead = state.selectedLead ?: return
        state.validateForSave()?.let { validation ->
            _uiState.value = state.copy(error = validation)
            return
        }
        val email = lead.email?.takeIf { it.isNotBlank() } ?: run {
            _uiState.value = state.copy(error = "This lead does not have an email address.")
            return
        }
        val body = state.emailFollowUpBody
        val subject = state.emailSubject
        val selectedFiles = state.selectedPropertyFiles
        val selectedPhotos = state.selectedPhotoDataUrls.takeIf { state.showFileSelection }.orEmpty()
        if (selectedFiles.isNotEmpty() || selectedPhotos.isNotEmpty()) {
            _uiState.value = state.copy(savingChannel = FollowUpChannel.Email, error = null)
            scope.launch {
                val fileAttachments = selectedFiles.map { file ->
                    val bytes = attachmentRepository.downloadAttachment(file).getOrElse { error ->
                        _uiState.value = _uiState.value.copy(savingChannel = null, error = error.havnFriendlyMessage("Could not prepare selected files."))
                        return@launch
                    }
                    HavnEmailAttachment(
                        fileName = file.fileName,
                        contentType = file.contentType ?: "application/octet-stream",
                        bytes = bytes
                    )
                }
                val photoAttachments = selectedPhotos.toEmailPhotoAttachments().getOrElse { error ->
                    _uiState.value = _uiState.value.copy(savingChannel = null, error = error.havnFriendlyMessage("Could not prepare selected photos."))
                    return@launch
                }
                val emailAttachments = fileAttachments + photoAttachments
                val opened = HavnExternalActions.openEmailWithAttachments(
                    to = email,
                    subject = subject,
                    body = body,
                    attachments = emailAttachments
                )
                if (!opened) {
                    _uiState.value = _uiState.value.copy(savingChannel = null, error = "Unable to open the email app.")
                    return@launch
                }
                saveSelectedDraftForChannel(FollowUpChannel.Email, markCompleted = true)
            }
            return
        }
        val opened = HavnExternalActions.openEmail(to = email, subject = subject, body = body)
        if (!opened) {
            _uiState.value = state.copy(error = "Unable to open the email app.")
            return
        }
        saveSelectedDraftForChannel(FollowUpChannel.Email, markCompleted = true)
    }

    fun sendSelectedDraftSms() {
        val state = _uiState.value
        val lead = state.selectedLead ?: return
        val baseBody = state.smsFollowUpBody
        if (baseBody.isBlank()) {
            _uiState.value = state.copy(error = "Draft body cannot be empty.")
            return
        }
        val phone = lead.phone?.takeIf { it.isNotBlank() } ?: run {
            _uiState.value = state.copy(error = "This lead does not have a phone number.")
            return
        }
        val selectedFiles = state.selectedPropertyFiles
        val selectedPhotos = state.selectedPhotoDataUrls.takeIf { state.showFileSelection }.orEmpty()
        if (selectedFiles.isNotEmpty() || selectedPhotos.isNotEmpty()) {
            _uiState.value = state.copy(savingChannel = FollowUpChannel.Sms, error = null)
            scope.launch {
                val body = baseBody.withSelectedFileLinks(selectedFiles).getOrElse { error ->
                    _uiState.value = _uiState.value.copy(savingChannel = null, error = error.havnFriendlyMessage("Could not prepare selected files."))
                    return@launch
                }
                val smsPhotos = selectedPhotos.toSmsPhotoDataUrls().getOrElse { error ->
                    _uiState.value = _uiState.value.copy(savingChannel = null, error = error.havnFriendlyMessage("Could not prepare selected photos."))
                    return@launch
                }
                val opened = if (smsPhotos.isNotEmpty()) {
                    HavnExternalActions.openSmsWithPhotos(to = phone, body = body, photoDataUrls = smsPhotos)
                } else {
                    HavnExternalActions.openSms(to = phone, body = body)
                }
                if (!opened) {
                    _uiState.value = _uiState.value.copy(savingChannel = null, error = "Unable to open the messages app.")
                    return@launch
                }
                saveSelectedDraftForChannel(FollowUpChannel.Sms, markCompleted = true)
            }
            return
        }
        val body = baseBody
        val opened = HavnExternalActions.openSms(to = phone, body = body)
        if (!opened) {
            _uiState.value = state.copy(error = "Unable to open the messages app.")
            return
        }
        saveSelectedDraftForChannel(FollowUpChannel.Sms, markCompleted = true)
    }

    private fun saveSelectedDraftForChannel(channel: FollowUpChannel, markCompleted: Boolean = false) {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        val validation = state.validateForSave()
        if (validation != null) {
            _uiState.value = state.copy(error = validation)
            return
        }

        val selectedLead = state.selectedLead ?: return
        val visitorId = selectedLead.visitorId ?: run {
            _uiState.value = state.copy(error = "This lead does not have a check-in yet.")
            return
        }
        val body = if (channel == FollowUpChannel.Email) state.emailFollowUpBody else state.sendFollowUpPreview
        val subject = if (channel == FollowUpChannel.Email) {
            state.emailSubject.ifBlank { "Following up on ${state.form.propertyAddress}" }
        } else {
            "SMS follow-up for ${state.form.visitorName}"
        }
        val sentAt = if (markCompleted) nowIsoString() else null
        val dueAt = state.form.dueAt
            .ifBlank { state.form.completedAt }
            .ifBlank { sentAt.orEmpty() }
            .ifBlank { nowIsoString() }

        _uiState.value = state.copy(isSaving = true, savingChannel = channel, error = null)
        scope.launch {
            followUpRepository.saveDraft(
                agentId = currentAgentId,
                draft = FollowUpDraft(
                    visitorId = visitorId,
                    channel = channel,
                    dueAt = dueAt,
                    subject = subject,
                    body = body,
                    status = if (markCompleted) FollowUpStatus.Completed else FollowUpStatus.Pending,
                    completedAt = sentAt
                )
            ).onSuccess { followUp ->
                val updatedFollowUps = listOf(followUp) + _uiState.value.followUps.filterNot { it.id == followUp.id }
                _uiState.value = _uiState.value.copy(
                    isSaving = false,
                    savingChannel = null,
                    savedFollowUpId = followUp.id,
                    followUps = updatedFollowUps,
                    completedFollowUpEventId = if (followUp.status == FollowUpStatus.Completed) followUp.id else _uiState.value.completedFollowUpEventId
                )
            }.onFailure {
                _uiState.value = _uiState.value.copy(isSaving = false, savingChannel = null)
                setError(it)
            }
        }
    }

    fun markSavedDraftSent() {
        val followUpId = _uiState.value.savedFollowUpId ?: return
        markSent(followUpId, _uiState.value.form.completedAt)
    }

    fun markSent(followUpId: String, completedAt: String) {
        val currentAgentId = agentId ?: return
        if (completedAt.isBlank()) {
            _uiState.value = _uiState.value.copy(error = "Enter the sent time before marking a follow-up as sent.")
            return
        }

        _uiState.value = _uiState.value.copy(isSaving = true, savingChannel = null, error = null)
        scope.launch {
            followUpRepository.markSent(currentAgentId, followUpId, completedAt)
                .onSuccess { followUp ->
                    replaceFollowUp(followUp)
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        savingChannel = null,
                        completedFollowUpEventId = followUp.id
                    )
                }
                .onFailure {
                    _uiState.value = _uiState.value.copy(isSaving = false, savingChannel = null)
                    setError(it)
                }
        }
    }

    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    private suspend fun String.withSelectedFileLinks(files: List<HavnAttachment>): Result<String> {
        if (files.isEmpty()) return Result.success(this)
        return attachmentRepository.shareUrlsForAttachments(files).map { signedFiles ->
            buildString {
                append(this@withSelectedFileLinks.trim())
                append("\n\nSelected files:")
                signedFiles.forEachIndexed { index, (file, url) ->
                    append("\n")
                    append(index + 1)
                    append(". ")
                    append(file.fileName)
                    append(" - ")
                    append(url)
                }
                append("\n\nFile links expire in 7 days.")
            }
        }
    }

    private suspend fun List<String>.toEmailPhotoAttachments(): Result<List<HavnEmailAttachment>> = runCatching {
        distinct().mapIndexedNotNull { index, photo ->
            val image = photo.toFollowUpPhotoPayload() ?: return@mapIndexedNotNull null
            HavnEmailAttachment(
                fileName = "havn-property-photo-${index + 1}.${image.contentType.photoExtension()}",
                contentType = image.contentType,
                bytes = image.bytes
            )
        }
    }

    @OptIn(ExperimentalEncodingApi::class)
    private suspend fun List<String>.toSmsPhotoDataUrls(): Result<List<String>> = runCatching {
        distinct().mapNotNull { photo ->
            val image = photo.toFollowUpPhotoPayload() ?: return@mapNotNull null
            "data:${image.contentType};base64,${Base64.encode(image.bytes)}"
        }
    }

    private suspend fun String.toFollowUpPhotoPayload(): FollowUpPhotoPayload? {
        val value = trim()
        if (value.isBlank()) return null
        if (value.startsWith("data:", ignoreCase = true)) {
            val bytes = value.decodeHavnDataUrlBytes()?.takeIf { it.isNotEmpty() } ?: return null
            return FollowUpPhotoPayload(
                bytes = bytes,
                contentType = value.havnDataUrlContentType()
            )
        }
        if (!value.isPublicTextLink()) return null
        val bytes = httpClient.get(value).bodyAsBytes().takeIf { it.isNotEmpty() } ?: return null
        return FollowUpPhotoPayload(
            bytes = bytes,
            contentType = value.inferImageContentType()
        )
    }

    fun consumeCompletedFollowUpEvent() {
        _uiState.value = _uiState.value.copy(completedFollowUpEventId = null)
    }

    fun dispose() {
        scope.cancel()
    }

    private suspend fun loadData(currentAgentId: String) {
        _uiState.value = _uiState.value.copy(isLoading = true, error = null)
        val followUpsResult = followUpRepository.followUpsForAgent(currentAgentId)

        if (followUpsResult.isFailure) {
            _uiState.value = _uiState.value.copy(isLoading = false)
            setError(followUpsResult.exceptionOrNull() ?: IllegalStateException("Unable to load follow-ups."))
            return
        }

        _uiState.value = _uiState.value.copy(
            isLoading = false,
            followUps = followUpsResult.getOrThrow()
        )
        val leadsResult = leadRepository.leadsForAgent(currentAgentId)
        if (leadsResult.isFailure) {
            setError(leadsResult.exceptionOrNull() ?: IllegalStateException("Unable to load leads."))
            return
        }
        _uiState.value = _uiState.value.copy(leads = leadsResult.getOrThrow())
        val selectedLeadId = _uiState.value.selectedLeadId
        val selectedLead = _uiState.value.leads.firstOrNull { it.id == selectedLeadId }
        if (selectedLead != null && _uiState.value.form.visitorName.isBlank()) {
            applyLeadSelection(selectedLead)
        }
    }

    private fun replaceFollowUp(followUp: FollowUp) {
        val existing = _uiState.value.followUps
        val updated = if (existing.any { it.id == followUp.id }) {
            existing.map { if (it.id == followUp.id) followUp else it }
        } else {
            listOf(followUp) + existing
        }
        _uiState.value = _uiState.value.copy(
            followUps = updated
        )
    }

    private fun setError(error: Throwable) {
        HavnLogger.error("FollowUpsViewModel", "User-visible follow-up error", error)
        _uiState.value = _uiState.value.copy(
            error = error.havnFriendlyMessage("Could not load follow-ups. Please try again.")
        )
    }
}

@OptIn(ExperimentalTime::class)
private fun nowIsoString(): String = Clock.System.now().toString()

data class FollowUpsUiState(
    val isLoading: Boolean = false,
    val isGenerating: Boolean = false,
    val isSaving: Boolean = false,
    val error: String? = null,
    val mode: FollowUpsMode = FollowUpsMode.Queue,
    val leads: List<Lead> = emptyList(),
    val followUps: List<FollowUp> = emptyList(),
    val selectedLeadId: String? = null,
    val form: FollowUpGenerationForm = FollowUpGenerationForm(),
    val generatedDraft: AiFollowUpDraft? = null,
    val previewChannel: FollowUpChannel = FollowUpChannel.Sms,
    val selectedMessageType: FollowUpMessageType = FollowUpMessageType.ThankYou,
    val subjectOverride: String? = null,
    val bodyOverride: String? = null,
    val savingChannel: FollowUpChannel? = null,
    val availablePhotoDataUrls: List<String> = emptyList(),
    val selectedPhotoDataUrls: List<String> = emptyList(),
    val availablePropertyFiles: List<HavnAttachment> = emptyList(),
    val selectedPropertyFileIds: List<String> = emptyList(),
    val savedFollowUpId: String? = null,
    val completedFollowUpEventId: String? = null
) {
    val selectedLead: Lead?
        get() = leads.firstOrNull { it.id == selectedLeadId }

    val pendingCount: Int
        get() = followUps.count { it.status == FollowUpStatus.Pending }

    val sentCount: Int
        get() = followUps.count { it.status == FollowUpStatus.Completed }

    val selectedPreviewBody: String
        get() = when (previewChannel) {
            FollowUpChannel.Email -> generatedDraft?.emailDraft.orEmpty()
            else -> generatedDraft?.smsDraft.orEmpty()
        }

    val emailSubject: String
        get() = subjectOverride ?: generatedDraft?.emailSubject?.takeIf { it.isNotBlank() }.orEmpty()

    val sendFollowUpPreview: String
        get() = bodyOverride ?: generatedDraft?.emailDraft?.takeIf { it.isNotBlank() }.orEmpty()

    val emailFollowUpBody: String
        get() = sendFollowUpPreview

    val smsFollowUpBody: String
        get() = sendFollowUpPreview

    val showPhotoSelection: Boolean
        get() = selectedMessageType == FollowUpMessageType.PropertyBrochure && availablePhotoDataUrls.isNotEmpty()

    val selectedPropertyFiles: List<HavnAttachment>
        get() = availablePropertyFiles.filter { it.id in selectedPropertyFileIds }

    val showFileSelection: Boolean
        get() = selectedMessageType == FollowUpMessageType.PropertyBrochure &&
            (availablePhotoDataUrls.isNotEmpty() || availablePropertyFiles.isNotEmpty())

    fun validateForSave(): String? = when {
        selectedLeadId == null -> "Select a lead before saving a follow-up."
        sendFollowUpPreview.isBlank() -> "Draft body cannot be empty."
        emailSubject.isBlank() -> "Subject cannot be empty."
        previewChannel !in listOf(FollowUpChannel.Sms, FollowUpChannel.Email) -> "Choose SMS or email."
        else -> null
    }
}

private fun String.isPublicTextLink(): Boolean {
    val value = trim()
    return value.startsWith("http://", ignoreCase = true) || value.startsWith("https://", ignoreCase = true)
}

private data class FollowUpPhotoPayload(
    val bytes: ByteArray,
    val contentType: String
)

private fun String.inferImageContentType(): String {
    val path = substringBefore('?').lowercase()
    return when {
        path.endsWith(".png") -> "image/png"
        path.endsWith(".webp") -> "image/webp"
        path.endsWith(".avif") -> "image/avif"
        path.endsWith(".heic") -> "image/heic"
        else -> "image/jpeg"
    }
}

private fun String.photoExtension(): String = when {
    contains("png", ignoreCase = true) -> "png"
    contains("webp", ignoreCase = true) -> "webp"
    contains("avif", ignoreCase = true) -> "avif"
    else -> "jpg"
}

private fun Lead.withPreservedPhotos(existing: Lead): Lead {
    if (sourceProperty.photoDataUrls.isNotEmpty()) return this
    return withPhotoDataUrls(existing.sourceProperty.photoDataUrls)
}

private fun Lead.withPhotoDataUrls(photoDataUrls: List<String>): Lead =
    copy(sourceProperty = sourceProperty.copy(photoDataUrls = photoDataUrls))

private fun String.subjectLine(): String =
    lineSequence()
        .firstOrNull { it.trimStart().startsWith("Subject:", ignoreCase = true) }
        ?.substringAfter(":")
        ?.trim()
        .orEmpty()

private fun String.bodyWithoutSubject(): String =
    lineSequence()
        .dropWhile { it.trim().isEmpty() }
        .drop(1)
        .dropWhile { it.trim().isEmpty() }
        .joinToString("\n")
        .trim()

data class FollowUpGenerationForm(
    val visitorName: String = "",
    val propertyAddress: String = "",
    val visitorInterest: String = "",
    val notes: String = "",
    val leadStatus: String = "Warm",
    val agentName: String = "HAVN agent",
    val languageCode: String = HavnLanguage.English.code,
    val languageName: String = HavnLanguage.English.displayName,
    val languageNativeName: String = HavnLanguage.English.nativeName,
    val dueAt: String = "",
    val completedAt: String = ""
) {
    fun validateForGeneration(): String? = when {
        visitorName.isBlank() -> "Lead name is required."
        propertyAddress.isBlank() -> "Property address is required."
        leadStatus.isBlank() -> "Lead status is required."
        else -> null
    }

    fun toAiInput(messageType: FollowUpMessageType): AiFollowUpInput = AiFollowUpInput(
        visitorName = visitorName,
        propertyAddress = propertyAddress,
        visitorInterest = visitorInterest,
        notes = notes,
        leadStatus = leadStatus,
        agentName = agentName,
        messageType = messageType.promptValue,
        tone = "warm, concise, professional",
        languageCode = languageCode,
        languageName = languageName,
        languageNativeName = languageNativeName
    )
}

enum class FollowUpsMode {
    Queue,
    Generate,
    Preview
}

enum class FollowUpMessageType {
    ThankYou,
    PropertyInfo,
    FollowUp,
    CheckIn,
    PriceUpdate,
    PropertyBrochure
}

private val FollowUpMessageType.promptValue: String
    get() = when (this) {
        FollowUpMessageType.ThankYou -> "Thank you"
        FollowUpMessageType.PropertyInfo -> "Property info"
        FollowUpMessageType.FollowUp -> "Follow-up"
        FollowUpMessageType.CheckIn -> "Check in"
        FollowUpMessageType.PriceUpdate -> "Price update"
        FollowUpMessageType.PropertyBrochure -> "Files"
    }
